import type { GanttItem } from "@/data/projects";
import type { EditableGanttTask } from "@/store/projectStore";

/** Календарные даты YYYY-MM-DD. */
export type DateEnvelope = { start: string; end: string };

export function normalizeIsoDate(s: string): string | null {
  const t = s.trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : null;
}

/** Объединённый интервал всех полос общего ганта с данным projectId (min start, max end). */
export function mainGanttEnvelope(projectId: string, items: GanttItem[]): DateEnvelope | null {
  const rows = items.filter((g) => g.projectId === projectId);
  if (rows.length === 0) return null;
  let minS = rows[0]!.start.slice(0, 10);
  let maxE = rows[0]!.end.slice(0, 10);
  for (const r of rows) {
    const s = r.start.slice(0, 10);
    const e = r.end.slice(0, 10);
    if (s < minS) minS = s;
    if (e > maxE) maxE = e;
  }
  return { start: minS, end: maxE };
}

export function mainGanttBarCount(projectId: string, items: GanttItem[]): number {
  return items.filter((g) => g.projectId === projectId).length;
}

/** Охват детального плана по задачам (min start, max end). */
export function internalPlanEnvelope(tasks: EditableGanttTask[]): DateEnvelope | null {
  const dates: string[] = [];
  for (const t of tasks) {
    const a = normalizeIsoDate(t.start);
    const b = normalizeIsoDate(t.end);
    if (a) dates.push(a);
    if (b) dates.push(b);
  }
  if (dates.length === 0) return null;
  return {
    start: dates.reduce((m, d) => (d < m ? d : m)),
    end: dates.reduce((m, d) => (d > m ? d : m)),
  };
}

/** Сводки интервалов не совпадают (как в плане: сравнение границ охвата). */
export function envelopesMismatch(roadmap: DateEnvelope, plan: DateEnvelope): boolean {
  return roadmap.start !== plan.start || roadmap.end !== plan.end;
}

/** План вылезает за границы объединённого интервала дорожной карты (начинается раньше или заканчивается позже). */
export function planOutsideRoadmap(roadmap: DateEnvelope, plan: DateEnvelope): boolean {
  return plan.start < roadmap.start || plan.end > roadmap.end;
}

/** Задачи, у которых интервал выходит за объединённый интервал дорожной карты. */
export function tasksOutsideRoadmapEnvelope(
  roadmap: DateEnvelope,
  tasks: EditableGanttTask[],
): { id: string; title: string }[] {
  const out: { id: string; title: string }[] = [];
  for (const t of tasks) {
    const s = normalizeIsoDate(t.start);
    const e = normalizeIsoDate(t.end);
    if (!s || !e) continue;
    if (s < roadmap.start || e > roadmap.end) {
      out.push({ id: t.id, title: t.title });
    }
  }
  return out;
}

export function formatRuDateRange(start: string, end: string): string {
  const opt: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
  try {
    const a = new Date(start + "T12:00:00");
    const b = new Date(end + "T12:00:00");
    return `${a.toLocaleDateString("ru-RU", opt)} — ${b.toLocaleDateString("ru-RU", opt)}`;
  } catch {
    return `${start} — ${end}`;
  }
}

/** Уникальные id инициатив (полосы общего ганта). */
export function mainGanttProjectIdSet(items: GanttItem[]): Set<string> {
  return new Set(items.map((g) => g.projectId));
}

/**
 * Только проекты, для которых на общем ганте есть полоса.
 * Если `ganttItems` ещё пуст, используется эталон из сида (`fallbackGanttItems`).
 */
export function filterProjectsByMainGantt<T extends { id: string }>(
  projects: T[],
  ganttItems: GanttItem[],
  fallbackGanttItems: GanttItem[],
): T[] {
  const source = ganttItems.length > 0 ? ganttItems : fallbackGanttItems;
  if (source.length === 0) return projects;
  const ids = mainGanttProjectIdSet(source);
  return projects.filter((p) => ids.has(p.id));
}
